import { z } from 'zod';
import { RENEWAL_REQUEST_STATUS, TICKET_PRIORITY, TICKET_STATUS, } from '../enums';
import { moneyMinorSchema } from '../api';
/**
 * The support desk: tickets and subscription renewals (SRS 5.3).
 *
 * Three audiences share these shapes — the hospital's own staff, the account's
 * contact person, and the owner console — so the DTOs live together rather than
 * being split by caller. What differs between them is authorisation and which
 * fields come back, never the vocabulary.
 */
// ── Attachments ────────────────────────────────────────────────────────────
/** 25 MB: a screenshot or a scanned deposit slip, not a database dump. */
export const MAX_DESK_ATTACHMENT_BYTES = 25 * 1024 * 1024;
/**
 * What a support screenshot is allowed to be.
 *
 * An allowlist rather than a blocklist, and no PDF-with-script surprises: these
 * files are opened by staff on the strength of a stranger having uploaded them.
 */
export const DESK_ATTACHMENT_MIME = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
    'application/pdf',
];
export const deskPresignSchema = z.object({
    filename: z.string().trim().min(1).max(200),
    contentType: z.enum(DESK_ATTACHMENT_MIME),
    sizeBytes: z.number().int().min(1).max(MAX_DESK_ATTACHMENT_BYTES),
});
/** A file the client has already uploaded to the key the presign handed back. */
export const deskAttachmentSchema = z.object({
    fileKey: z.string().trim().min(3).max(400),
    fileName: z.string().trim().min(1).max(200),
    mimeType: z.enum(DESK_ATTACHMENT_MIME),
    sizeBytes: z.number().int().min(1).max(MAX_DESK_ATTACHMENT_BYTES),
});
// ── Tickets ────────────────────────────────────────────────────────────────
const attachmentsField = z.array(deskAttachmentSchema).max(10).default([]);
export const TICKET_CATEGORIES = ['general', 'licence_change', 'payment_proof'];
export const createTicketSchema = z.object({
    subject: z.string().trim().min(5).max(200),
    body: z.string().trim().min(10).max(5000),
    priority: z.enum(TICKET_PRIORITY).default('normal'),
    category: z.enum(TICKET_CATEGORIES).default('general'),
    attachments: attachmentsField,
});
/** Same as above, plus the hospital it is about — the portal serves several. */
export const createTicketForTenantSchema = createTicketSchema.extend({
    tenantId: z.string().uuid(),
});
export const ticketMessageSchema = z.object({
    body: z.string().trim().min(1).max(5000),
    attachments: attachmentsField,
    /**
     * Kept from the hospital. Only the contact person and the owner may set it,
     * and the tenant-side endpoint ignores it outright.
     */
    isInternal: z.boolean().default(false),
});
export const resolveTicketSchema = z.object({
    note: z.string().trim().min(5).max(2000),
});
export const escalateTicketSchema = z.object({
    note: z.string().trim().min(5).max(2000),
    priority: z.enum(TICKET_PRIORITY).optional(),
});
export const setTicketStatusSchema = z.object({
    status: z.enum(TICKET_STATUS),
    note: z.string().trim().max(2000).optional(),
});
export const ticketListQuerySchema = z.object({
    tenantId: z.string().uuid().optional(),
    status: z.enum(TICKET_STATUS).optional(),
    /** Owner console shortcut: only what a contact person handed up. */
    escalatedOnly: z.coerce.boolean().optional(),
    q: z.string().trim().max(120).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().optional(),
});
// ── Subscription renewals ──────────────────────────────────────────────────
export const createRenewalRequestSchema = z.object({
    tenantId: z.string().uuid(),
    amountMinor: moneyMinorSchema.refine((v) => v > 0, 'Enter the amount that was paid'),
    currency: z.string().length(3).default('PKR'),
    reference: z.string().trim().max(120).optional(),
    note: z.string().trim().max(2000).optional(),
    /** The transfer screenshot. Required — this is the whole point of the request. */
    attachments: z.array(deskAttachmentSchema).min(1).max(5),
});
export const renewalListQuerySchema = z.object({
    tenantId: z.string().uuid().optional(),
    status: z.enum(RENEWAL_REQUEST_STATUS).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().optional(),
});
// ── Contact person ─────────────────────────────────────────────────────────
export const assignContactSchema = z.object({
    totpCode: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code'),
    reason: z.string().trim().min(5).max(400),
    /** Null clears the assignment and leaves the hospital without a contact. */
    contactUserId: z.string().uuid().nullable(),
});
//# sourceMappingURL=support-desk.js.map